import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { CheckCircle, Users, Calendar, Download, FileText } from "lucide-react";
import api from "../../api/client";
import { glassCardHover } from "../../utils/glassStyles";
import RecruiterPageShell from "../../components/recruiter/RecruiterPageShell";
import RecruiterEmptyState from "../../components/recruiter/RecruiterEmptyState";
import Loader from "../../components/ui/Loader";

export default function AcceptedApplications() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    fetchAcceptedApplications();
  }, []);

  useEffect(() => {
    if (search) {
      const filtered = applications.filter((app) => {
        const profile = app.applier?.applierProfile;
        return (
          profile?.fullName?.toLowerCase().includes(search.toLowerCase()) ||
          app.applier?.email?.toLowerCase().includes(search.toLowerCase()) ||
          app.job?.title?.toLowerCase().includes(search.toLowerCase())
        );
      });
      setFilteredApps(filtered);
    } else {
      setFilteredApps(applications);
    }
  }, [search, applications]);

  const fetchAcceptedApplications = async () => {
    try {
      const res = await api.get("/recruiter/applications");
      if (!res.data || !Array.isArray(res.data.data)) {
        setError("Invalid data format received.");
        return;
      }
      const accepted = res.data.data.filter((app) => app.status === "ACCEPTED");
      setApplications(accepted);
      setFilteredApps(accepted);
    } catch (err) {
      console.error("Failed to load accepted applications:", err);
      setError("Failed to load accepted applications");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader label="Loading accepted applications..." />;
  }

  return (
    <RecruiterPageShell
      title="Accepted Applications"
      subtitle="View all candidates you've accepted across all job postings"
      onBack={() => navigate("/recruiter/dashboard")}
      actions={
        <div className="rounded-2xl px-5 py-3 text-center border border-green-500/20 bg-green-500/10 backdrop-blur-sm">
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{filteredApps.length}</p>
          <p className="text-xs text-muted-foreground">Total Accepted</p>
        </div>
      }
    >
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="mb-6">
        <Input
          placeholder="Search by name, email, or job title..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md h-12 rounded-xl"
        />
      </div>

      {filteredApps.length === 0 ? (
        <RecruiterEmptyState
          icon={Users}
          title={search ? "No matches found" : "No accepted applications yet"}
          description={
            search
              ? "Try a different search term."
              : "Accepted candidates will appear here once you approve applications."
          }
          actionLabel={search ? undefined : "View Applications"}
          onAction={search ? undefined : () => navigate("/recruiter/applications")}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredApps.map((app) => {
            const profile = app.applier?.applierProfile;
            const resumeUrl = profile?.resumeUrl || profile?.resume;

            return (
              <Card key={app.id} className={`rounded-2xl border-green-500/20 ${glassCardHover}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg font-bold truncate">
                        {profile?.fullName || "N/A"}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1 truncate">{app.applier?.email}</p>
                    </div>
                    <Badge className="bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30 shrink-0">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Accepted
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
                      Job Position
                    </p>
                    <p
                      className="text-sm font-medium text-primary cursor-pointer hover:underline"
                      onClick={() => navigate(`/recruiter/jobs/${app.jobId}/applicants`)}
                    >
                      {app.job?.title || "N/A"}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
                        University
                      </p>
                      <p className="text-sm font-medium">{profile?.university || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">GPA</p>
                      <p className="text-sm font-medium">{profile?.gpa || "N/A"}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Applied On
                    </p>
                    <p className="text-sm font-medium">
                      {new Date(app.appliedAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  </div>

                  <div className="flex gap-2 pt-2">
                    {resumeUrl && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const url = resumeUrl.startsWith("http")
                            ? resumeUrl
                            : `${API_BASE_URL}${resumeUrl}`;
                          window.open(url, "_blank");
                        }}
                        className="flex-1"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Resume
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => navigate(`/recruiter/applications/${app.id}`)}
                      className="flex-1"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </RecruiterPageShell>
  );
}
